import * as routerNames from '@/routerNames';

import Vue from 'vue';
import Router from 'vue-router';
import { Route } from 'vue-router/types/router';

import { setPendingAccountRecoveryToken, stripAccountRecoveryTokenFromUrl } from '@/services/accountRecoveryUrl';
import AuthMiddleware from '@/middlewares/AuthMiddleware';
import AdminMiddleware from '@/middlewares/AdminMiddleware';
import PaidCompanyMiddleware from '@/middlewares/PaidCompanyMiddleware';
import SyncCompanyMiddleware from '@/middlewares/SyncCompanyMiddleware';
import RedirectMiddleware from '@/middlewares/RedirectMiddleware';

import CheckoutPage from '@/components/pages/billing/CheckoutPage/CheckoutPage.vue';
import PlansPage from '@/components/pages/billing/PlansPage/PlansPage.vue';
import HomePage from '@/components/pages/HomePage/HomePage.vue';
import MeetingsPage from '@/components/pages/meetings/MeetingsPage.vue';
import BillingHistoryPage from '@/components/pages/settings/BillingHistoryPage/BillingHistoryPage.vue';
import CompanyNotPaid from '@/components/pages/views/CompanyNotPaid.vue';
import LoginSocialCallbackPage from '@/components/pages/auth/LoginSocialCallbackPage.vue';
import BitbucketOAuthCallback from '@/components/connectors/BitbucketOAuthCallback.vue';
import ConfluenceOAuthCallback from '@/components/connectors/ConfluenceOAuthCallback.vue';
import GdriveOAuthCallback from '@/components/connectors/GdriveOAuthCallback.vue';
import GitHubOAuthCallback from '@/components/connectors/GitHubOAuthCallback.vue';
import UserProfilePage from '@/components/pages/settings/UserProfilePage/UserProfilePage.vue';
import ActiveSessionsPage from '@/components/pages/settings/ActiveSessionsPage/ActiveSessionsPage.vue';
import LoginSocialFailPage from '@/components/pages/auth/LoginSocialFailPage.vue';
import LoginSocialTwoFactorPage from '@/components/pages/auth/LoginSocialTwoFactorPage.vue';
import AdminPanel from '@/components/pages/admin/AdminPanel.vue';
import AuthLayout from '@/components/pages/auth/AuthLayout.vue';
import WorkspacesPage from '@/components/pages/workspace/WorkspacesPage.vue';
import WorkspaceEditPage from '@/components/pages/workspace/WorkspaceEditPage/WorkspaceEditPage.vue';
import DraftsPage from '@/components/pages/draft/DraftsPage.vue';
import DraftEditPage from '@/components/pages/draft/DraftEditPage.vue';
import CompanyPage from '@/components/pages/settings/CompanyPage.vue';
import ArticleEditPage from '@/components/pages/article/ArticleEditPage.vue';
import UserManagement from '@/components/pages/settings/UserManagementPage/UserManagementPage.vue';
import BrowsePage from '@/components/pages/views/BrowsePage.vue';
import CompanyGroupsPage from '@/components/pages/settings/CompanyGroupsPage.vue';
import BillingPlanDetailsPage from '@/components/pages/settings/BillingPlanDetailsPage/BillingPlanDetailsPage.vue';
import CompanyDeactivated from '@/components/pages/views/CompanyDeactivated/CompanyDeactivated.vue';
import ArticleVersionEditPage from '@/components/pages/article/ArticleVersionEditPage.vue';
import UserDeactivatedInCompany from '@/components/pages/views/UserDeactivatedInCompany.vue';
import CompanyNotPaidForUser from '@/components/pages/views/CompanyNotPaidForUser.vue';
import RetentionPage from '@/components/pages/settings/RetentionPage/RetentionPage.vue';
import IntegrationsPage from '@/components/pages/settings/IntegrationsPage/IntegrationsPage.vue';
import OAuthConsentPage from '@/components/pages/oauth/OAuthConsentPage.vue';

export const routes = [
  {
    path: '/',
    name: routerNames.HOME,
    component: HomePage,
  },
  {
    path: '/b/shared/:sharedToken/:accessType(private)?',
    name: routerNames.BROWSE_SHARED,
    component: BrowsePage,
    props: true,
  },
  {
    path: '/b/:workspaceId/:workspaceSlug/:articleId?/:articleSlug?/:mode(notes|video)?',
    name: routerNames.BROWSE,
    component: BrowsePage,
    props: true,
  },
  {
    path: '/b/:workspaceId/:workspaceSlug/:articleId/:articleSlug/:paragraphSlug',
    name: routerNames.BROWSE_PARAGRAPH,
    component: BrowsePage,
    props: true,
  },
  {
    path: '/b/:workspaceId/:workspaceSlug/:articleId?/:articleSlug?/:mode(notes|video)?/:textTabSlug?',
    name: routerNames.BROWSE_TEXT_TAB,
    component: BrowsePage,
    props: true,
  },
  {
    path: '/workspaces',
    name: routerNames.WORKSPACES,
    component: WorkspacesPage,
  },
  {
    path: '/workspace/:id/edit',
    name: routerNames.WORKSPACE_EDIT,
    component: WorkspaceEditPage,
  },
  {
    path: '/article/:id/edit/:mode(notes|video)?',
    name: routerNames.ARTICLE_EDIT,
    component: ArticleEditPage,
  },
  {
    path: '/article/:id/v/:version/edit',
    name: routerNames.ARTICLE_VERSION_EDIT,
    component: ArticleVersionEditPage,
  },
  {
    path: '/article/new/:id?',
    name: routerNames.ARTICLE_CREATE,
    component: DraftEditPage,
  },
  {
    path: '/drafts',
    name: routerNames.DRAFTS,
    component: DraftsPage,
  },
  {
    path: '/draft/:id/:mode(notes|video)?',
    name: routerNames.DRAFT_EDIT,
    component: DraftEditPage,
  },
  {
    path: '/settings/profile',
    name: routerNames.USER_PROFILE,
    component: UserProfilePage,
  },
  {
    // SNAP-2389: view / terminate active sessions (ASVS 7.5.2).
    path: '/settings/sessions',
    name: routerNames.ACTIVE_SESSIONS,
    component: ActiveSessionsPage,
  },
  {
    path: '/oauth/consent',
    name: routerNames.OAUTH_CONSENT,
    component: OAuthConsentPage,
  },
  {
    path: '/settings/company',
    name: routerNames.COMPANY,
    component: CompanyPage,
  },
  {
    path: '/settings/users',
    name: routerNames.USER_MANAGEMENT,
    component: UserManagement,
  },
  {
    path: '/settings/groups',
    name: routerNames.COMPANY_GROUPS,
    component: CompanyGroupsPage,
  },
  {
    path: '/settings/billing/plan-details',
    name: routerNames.BILLING_PLAN_DETAILS,
    component: BillingPlanDetailsPage,
    props: true,
  },
  {
    path: '/settings/billing/history',
    name: routerNames.BILLING_HISTORY,
    component: BillingHistoryPage,
    props: true,
  },
  {
    path: '/signup/:token',
    name: routerNames.SIGNUP_INVITED,
    component: AuthLayout,
    props: true,
  },
  {
    path: '/signup',
    name: routerNames.SIGNUP,
    component: AuthLayout,
  },
  {
    // SNAP-2309: the API sets the session cookie on the Google redirect and carries no token here.
    path: '/login/social',
    name: routerNames.LOGIN_SOCIAL_CALLBACK,
    component: LoginSocialCallbackPage,
  },
  {
    // SNAP-2366 — the Google OAuth callback redirects here instead of
    // /login/social/:token when the user's company mandates 2FA. Nothing
    // redirects here until the API's ENFORCE_2FA_ON_GOOGLE_LOGIN flag is on,
    // which is why this route must ship BEFORE that flag is flipped.
    path: '/login/social-2fa/:loginToken',
    name: routerNames.LOGIN_SOCIAL_TWO_FACTOR,
    component: LoginSocialTwoFactorPage,
    props: true,
  },
  {
    path: '/login/social-fail/:message',
    name: routerNames.LOGIN_SOCIAL_FAIL,
    component: LoginSocialFailPage,
    props: true,
  },
  {
    path: '/connectors/bitbucket/callback',
    name: 'BitbucketOAuthCallback',
    component: BitbucketOAuthCallback,
  },
  {
    path: '/connectors/confluence/callback',
    name: 'ConfluenceOAuthCallback',
    component: ConfluenceOAuthCallback,
  },
  {
    path: '/connectors/gdrive/callback',
    name: 'GdriveOAuthCallback',
    component: GdriveOAuthCallback,
  },
  {
    path: '/connectors/github/callback',
    name: 'GitHubOAuthCallback',
    component: GitHubOAuthCallback,
  },
  {
    path: '/login/:token?/:articleId?/:companyId?/:destination?',
    name: routerNames.LOGIN,
    component: AuthLayout,
    props: true,
  },
  {
    path: '/password-reset/:token',
    name: routerNames.PASSWORD_RESET_TOKEN,
    component: AuthLayout,
    props: true,
  },
  {
    // SNAP-2424: lost-factor recovery. The emailed link lands here, the user sets a password, and the login email moves.
    path: '/account-recovery/:token',
    name: routerNames.ACCOUNT_RECOVERY_REDEEM,
    component: AuthLayout,
    props: true,
  },
  {
    // SNAP-2424: the cancel link mailed to the old address. Single use, and it stops working once the change applies.
    path: '/account-recovery/cancel/:token',
    name: routerNames.ACCOUNT_RECOVERY_CANCEL,
    component: AuthLayout,
    props: true,
  },
  {
    path: '/password-reset',
    name: routerNames.PASSWORD_RESET,
    component: AuthLayout,
  },
  {
    path: '/inactive',
    name: routerNames.INACTIVE_COMPANY,
    component: CompanyDeactivated,
  },
  {
    path: '/user-deactivated',
    name: routerNames.USER_DEACTIVATED,
    component: UserDeactivatedInCompany,
  },
  // Super admin
  {
    path: '/admin/:page?/:id?',
    name: routerNames.ADMIN_PANEL,
    component: AdminPanel,
    props: true,
  },
  // Billing
  {
    path: '/checkout',
    name: routerNames.CHECKOUT,
    component: CheckoutPage,
  },
  {
    path: '/subscription/:update?',
    name: routerNames.BILLING_PLANS,
    component: PlansPage,
    props: true,
  },
  {
    path: '/not-paid',
    name: routerNames.COMPANY_NOT_PAID,
    component: CompanyNotPaid,
  },
  {
    path: '/temporarily-deactivated',
    name: routerNames.COMPANY_NOT_PAID_FOR_USER,
    component: CompanyNotPaidForUser,
  },
  // Snaplore bot
  {
    path: '/meetings',
    name: routerNames.MEETINGS,
    component: MeetingsPage,
  },
  // Retention
  {
    path: '/retention',
    name: routerNames.RETENTION,
    component: RetentionPage,
  },
  // Integrations
  {
    path: '/settings/integrations',
    name: routerNames.INTEGRATIONS,
    component: IntegrationsPage,
  },
];

// Runs at import, before the first navigation and before Sentry is initialised in main.ts.
stripAccountRecoveryTokenFromUrl();

const router = new Router({ routes });

// A link pasted into an open tab changes only the fragment, so the load-time strip does not run for it.
// Capture the token and redirect to the redacted route, so the router never keeps the real token in the URL.
router.beforeEach((to, _from, next) => {
  const isRecoveryRoute =
    to.name === routerNames.ACCOUNT_RECOVERY_REDEEM || to.name === routerNames.ACCOUNT_RECOVERY_CANCEL;
  const token = to.params.token;
  if (isRecoveryRoute && token && token !== 'redacted') {
    setPendingAccountRecoveryToken(token);
    return next({ name: to.name, params: { token: 'redacted' }, replace: true });
  }
  next();
});

const originalPush = Router.prototype.push as any;
Router.prototype.push = function push(location: Route): Promise<Route> {
  return originalPush.call(this, location).catch(() => {});
};

Vue.use(Router);

// authentication guard

router.beforeEach(AuthMiddleware);

// company guard
router.beforeEach(PaidCompanyMiddleware);
router.beforeEach(SyncCompanyMiddleware);

router.beforeEach(AdminMiddleware);

router.beforeEach(RedirectMiddleware);

export default router;
